import type { AgentActivityState } from '@reflex/shared';
import type { Notification } from '@reflex/plugin-notifications/shared/types';
import type { DecisionGate } from '@reflex/plugin-api/services';
import { ATTENTION_STATUSES } from '@/lib/types';
import type { Agent, FlowRunSummary } from '@/lib/types';
import { getNonArchivedAgents } from '@/lib/agent-utils';
import { unseenActivityFor } from '@/hooks/agents/agent-activity-utils';

export type InboxItem =
  | { type: 'agent'; agent: Agent }
  | { type: 'decision-gate'; gate: DecisionGate }
  | { type: 'flow-run'; run: FlowRunSummary }
  | { type: 'notification'; notification: Notification };

export type Selection =
  | { type: 'agent'; agent: Agent }
  | { type: 'decision-gate'; gateId: string }
  | { type: 'flow-run'; runId: string }
  | { type: 'notification'; notificationId: string };

export const COMPLETED_LIMIT = 10;

export interface Buckets {
  /**
   * Agents that explicitly need user input (`needs_input`, `interrupted`)
   * plus pending decision gates (the neutral replacement for flow approvals).
   */
  needsInput: InboxItem[];
  /**
   * Agents whose durable status is `running` but whose turn has finished —
   * the devbox is waiting for the user. Agent-only.
   */
  idle: InboxItem[];
  failed: InboxItem[];
  completed: InboxItem[];
}

/**
 * Returns true when a `running` agent's turn has finished (the devbox is
 * waiting for the user). Uses the same evidence-gated rule as the unseen dot
 * and the inbox badge (`unseenActivityFor`), so the badge count equals the
 * items this page shows.
 */
function isAgentIdle(agent: Agent, activities: ReadonlyMap<string, AgentActivityState>): boolean {
  return agent.status === 'running' && unseenActivityFor(agent, activities) === 'idle';
}

export function buildBuckets(
  agents: Agent[],
  flowRuns: FlowRunSummary[],
  activities: ReadonlyMap<string, AgentActivityState> = new Map(),
  gates: DecisionGate[] = [],
): Buckets {
  const active = getNonArchivedAgents(agents);

  const agentNeedsInput: InboxItem[] = active
    .filter((a) => ATTENTION_STATUSES.has(a.status))
    .map((agent) => ({ type: 'agent', agent }));
  // Pending approvals now surface as neutral decision gates (the same items
  // Slack answers), not raw `waiting_approval` flow runs. Failed/completed
  // flow runs below are unchanged — those are history, not gates.
  const gateNeedsInput: InboxItem[] = [...gates]
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((gate) => ({ type: 'decision-gate', gate }));

  const agentIdle: InboxItem[] = active
    .filter((a) => isAgentIdle(a, activities))
    .map((agent) => ({ type: 'agent', agent }));

  const agentFailed: InboxItem[] = active
    .filter((a) => a.status === 'error')
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((agent) => ({ type: 'agent', agent }));
  const flowFailed: InboxItem[] = flowRuns
    .filter((r) => r.status === 'error')
    .sort((a, b) => b.createdAt - a.createdAt)
    .map((run) => ({ type: 'flow-run', run }));

  const agentCompleted: InboxItem[] = active
    .filter((a) => a.status === 'completed')
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, COMPLETED_LIMIT)
    .map((agent) => ({ type: 'agent', agent }));
  const flowCompleted: InboxItem[] = flowRuns
    .filter((r) => r.status === 'completed')
    .sort((a, b) => b.createdAt - a.createdAt)
    .slice(0, COMPLETED_LIMIT)
    .map((run) => ({ type: 'flow-run', run }));

  return {
    needsInput: [...agentNeedsInput, ...gateNeedsInput],
    idle: agentIdle,
    failed: [...agentFailed, ...flowFailed],
    completed: [...agentCompleted, ...flowCompleted],
  };
}

export function itemId(item: InboxItem): string {
  switch (item.type) {
    case 'agent':
      return `agent:${item.agent.id}`;
    case 'decision-gate':
      return `decision-gate:${item.gate.id}`;
    case 'flow-run':
      return `flow-run:${item.run.id}`;
    case 'notification':
      return `notification:${item.notification.id}`;
  }
}

export function selectionId(sel: Selection): string {
  switch (sel.type) {
    case 'agent':
      return `agent:${sel.agent.id}`;
    case 'decision-gate':
      return `decision-gate:${sel.gateId}`;
    case 'flow-run':
      return `flow-run:${sel.runId}`;
    case 'notification':
      return `notification:${sel.notificationId}`;
  }
}

export function computeAttentionCount(
  agents: Agent[],
  flowRuns: FlowRunSummary[],
  activities: ReadonlyMap<string, AgentActivityState> = new Map(),
  gates: DecisionGate[] = [],
): number {
  const active = getNonArchivedAgents(agents);
  const agentAttention = active.filter(
    (a) => ATTENTION_STATUSES.has(a.status) || isAgentIdle(a, activities),
  ).length;
  // Prefer the live gate count when available; fall back to raw
  // `waiting_approval` flow runs for callers that don't have gates loaded.
  const gateAttention =
    gates.length > 0
      ? gates.length
      : flowRuns.filter((r) => r.status === 'waiting_approval').length;
  return agentAttention + gateAttention;
}
